import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { AIAssistantsData } from '../../TopBar/types';
import { SidebarAIAssistantSwitcher, SidebarLogo, SidebarSettingsButton } from './components';
import { SidebarAgentGroup } from './components/SidebarAgentGroup';
import { SidebarChannelsGroup } from './components/SidebarChannelsGroup';
import { SidebarConversationsGroup } from './components/SidebarConversationsGroup';
import { SidebarPreviewButton } from './components/SidebarPreviewButton';
import { QueryKey } from '@/Config/api/queryKey';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import { TierPlanCard } from '@/Domain/components/TierPlanCard/TierPlanCard';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { cn } from '@/Domain/utils/cn';

export function AppSidebar() {
  const { state, isMobile } = useSidebar();
  // On mobile, always show expanded content since the Sheet is full-width
  const isCollapsed = state === 'collapsed' && !isMobile;

  const shortcuts = useMemo(() => ['1', '2', '3', '4', '5', '6', '7', '8'], []);

  const { data: aiAssistantsData } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  const scenarioIdPermissions = aiAssistantsData?.current_ai_assistant.permissions;

  const { data: subscriptionData } = useGetSubscription();
  const scenarioIdTier: TierEnum | undefined = subscriptionData?.current_plan.tier;

  useHotkeys(
    shortcuts,
    (event) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.repeat) return;

      const activeElement = document.activeElement as HTMLElement | null;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      const target = document.querySelector<HTMLElement>(`[data-shortcut="${event.key}"]`);
      if (!target) return;

      event.preventDefault();
      target.click();
    },
    [shortcuts],
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className={cn('h-[53px] border-b border-sidebar-border')}>
        <SidebarLogo />
      </SidebarHeader>

      <SidebarHeader>
        <SidebarAIAssistantSwitcher
          aiAssistantsData={aiAssistantsData}
          hasSubscriptionAccess={!!scenarioIdPermissions?.subscription}
        />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarPreviewButton />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <SidebarConversationsGroup
          isLocked={!scenarioIdPermissions?.conversations}
          simulationLocked={!scenarioIdPermissions?.simulation}
        />
        <SidebarAgentGroup isLocked={!scenarioIdPermissions?.ai_assistant} />
        <SidebarChannelsGroup isLocked={!scenarioIdPermissions?.ai_assistant} />
      </SidebarContent>

      <SidebarFooter>
        {scenarioIdTier && scenarioIdTier !== TierEnum.CUSTOM && (
          <TierPlanCard open={!isCollapsed} />
        )}

        <SidebarSettingsButton />
      </SidebarFooter>
    </Sidebar>
  );
}

export default AppSidebar;
